/**
 * Lane C0-PROPS round 2: the prop families of the user's kit sheets (props-kit-sheet-2, biomes-kit-sheet-2,
 * ground-kit-sheet-2; ideas only, our own designs) that round 1 lacked: walls with tile caps, gates in walls, steles,
 * banner and sign variety, lantern variety, bamboo and stone fences, footbridges, flowering shrubs and beds, bamboo
 * clumps, rock sets, lily ponds and a garden cascade, market goods, wheelbarrows and a covered wagon, furniture, archery
 * targets, war drums, plum-blossom posts, altars and wayside shrines, stone stupas and basins, small decor, canopies and
 * dyed cloth, docks and boats, crate/sack/barrel stacks and seasonal ground bits. Kept for towns B–D too
 * (docs/design/C0_TOWNS_BCD.md: mine, harbour, lantern town). Upright cards anchored at the foot, as props.ts.
 */
import { hash2, valueNoise } from '@nine/shared';
import { boat, box, lantern, M, snap, wall, type Ramp, type RGB } from '../buildings';
import type { Sprite } from '../scenery';
import { blob, clothBanner, K, propPix, tassel } from './parts';
import { barrelAt, fin, flat, jarAt, pole, stoneT, wood } from './propBase';

/** Blossom pink. */
const PINK: Ramp = [[0x5a, 0x1f, 0x3a], [0xa9, 0x48, 0x6c], [0xd9, 0x70, 0x8f], [0xf2, 0xa0, 0xb9], [0xff, 0xd9, 0xe4]];

const tiles = (r: Ramp) => (u: number, v: number, l: number): RGB =>
  snap(r, l + ((u | 0) % 4 < 2 ? 0.12 : -0.12), u | 0, v | 0, 0);

// ───────────────────────── walls and gates ─────────────────────────

/** A white wall with a dark footing and a grey tile cap, 2 tiles long along x or y (≈ 46 px). */
function wallSeg(along: 'x' | 'y', brickWall: boolean): Sprite {
  const p = propPix(84, 84, 42, 62);
  const L = 1;
  const r: [number, number, number, number] = along === 'x' ? [-L, -0.18, L, 0.18] : [-0.18, -L, 0.18, L];
  box(p.px, p.P, r, 0, 8, stoneT, stoneT);
  if (brickWall)
    box(p.px, p.P, r, 8, 40, (u, v, l) => {
      const row = Math.floor(v / 4);
      const off = row & 1 ? 4 : 0;
      if (v % 4 < 1 || (u + off) % 8 < 1) return snap(M.stone, l - 0.15, u | 0, v | 0, 0);
      return snap(M.brick, l + (hash2(Math.floor((u + off) / 8), row) - 0.5) * 0.25, u | 0, v | 0, 0);
    });
  else
    box(p.px, p.P, r, 8, 40, (u, v, l) =>
      snap(K.wallPlaster, l + 0.12 + (valueNoise(u / 8, v / 12) - 0.5) * 0.12 - (v > 34 ? 0.3 : 0) - (v < 12 ? 0.15 : 0), u | 0, v | 0),
    );
  // The cap: a little two-slope tile roof with a ridge.
  const c: [number, number, number, number] = along === 'x' ? [-L - 0.05, -0.32, L + 0.05, 0.32] : [-0.32, -L - 0.05, 0.32, L + 0.05];
  box(p.px, p.P, c, 40, 44, tiles(K.greyTile), tiles(K.greyTile));
  const rr: [number, number, number, number] = along === 'x' ? [-L, -0.08, L, 0.08] : [-0.08, -L, 0.08, L];
  box(p.px, p.P, rr, 44, 47, flat(K.greyTile, 0.1));
  return fin(p);
}

/** A crenellated town-wall section of grey brick with a walk-way rim (≈ 70 px), 2 tiles along x or y. */
function townWall(along: 'x' | 'y'): Sprite {
  const p = propPix(90, 110, 45, 86);
  const L = 1;
  const r: [number, number, number, number] = along === 'x' ? [-L, -0.4, L, 0.4] : [-0.4, -L, 0.4, L];
  box(p.px, p.P, r, 0, 58, (u, v, l) => {
    const row = Math.floor(v / 5);
    const off = row & 1 ? 6 : 0;
    if (v % 5 < 1 || (u + off) % 12 < 1) return snap(M.stone, l - 0.2, u | 0, v | 0, 0);
    return snap(M.stone, l - 0.05 + (hash2(Math.floor((u + off) / 12), row) - 0.5) * 0.22 + (valueNoise(u / 9, v / 14) > 0.72 ? -0.15 : 0), u | 0, v | 0);
  }, stoneT);
  for (let k = 0; k < 4; k++) {
    const t = -L + 0.25 + k * 0.5;
    const m: [number, number, number, number] = along === 'x' ? [t - 0.15, 0.1, t + 0.15, 0.4] : [0.1, t - 0.15, 0.4, t + 0.15];
    box(p.px, p.P, m, 58, 68, stoneT, stoneT);
  }
  return fin(p);
}

/** A gate in a wall: a round-topped opening with red leaves, a plaque and a little tiled roof (≈ 72 px). */
function wallGate(): Sprite {
  const p = propPix(96, 110, 48, 84);
  box(p.px, p.P, [-1.3, -0.25, 1.3, 0.25], 0, 52, (u, v, l) => snap(K.wallPlaster, l + 0.1 - (v > 46 ? 0.3 : 0), u | 0, v | 0), stoneT);
  wall(p.px, p.P, 'left', 0.26, -0.55, 0.55, 0, 40, (u, v) => {
    const top = 30 + Math.sqrt(Math.max(0, 1 - (u / 8.8) ** 2)) * 10;
    if (v > top) return null;
    if (v > top - 2) return snap(M.stone, 0.8, u | 0, v | 0, 0);
    return Math.abs(u) < 0.7 ? M.dark[1]! : (u | 0) % 5 === 0 && (v | 0) % 8 === 4 ? M.gold[3]! : snap(M.lacquer, 0.5, u | 0, v | 0);
  });
  wall(p.px, p.P, 'left', 0.27, -0.4, 0.4, 42, 50, (u, v) => (hash2(u | 0, v | 0) > 0.6 ? M.gold[3]! : M.dark[1]!));
  box(p.px, p.P, [-1.5, -0.45, 1.5, 0.45], 52, 56, tiles(K.greyTile), tiles(K.greyTile));
  box(p.px, p.P, [-1.2, -0.25, 1.2, 0.25], 56, 62, tiles(K.greyTile), tiles(K.greyTile));
  box(p.px, p.P, [-1.25, -0.08, 1.25, 0.08], 62, 65, flat(K.greyTile, 0.1));
  lantern(p.px, p.P(-0.75, 0.3, 46), 2);
  lantern(p.px, p.P(0.75, 0.3, 46), 2);
  return fin(p);
}

/** A stone stele on a tortoise base (≈ 50 px). */
function stele(): Sprite {
  const p = propPix(44, 70, 22, 60);
  blob(p.px, p.ax, p.ay - 5, 14, 6, M.stone, 0.5);
  blob(p.px, p.ax + 12, p.ay - 6, 4, 3, M.stone, 0.6);
  box(p.px, p.P, [-0.35, -0.1, 0.35, 0.1], 8, 46, (u, v, l) => (((v | 0) % 6 < 3 && (u | 0) % 3 !== 0 && v > 14 && v < 40 && Math.abs(u) < 4) ? M.stone[1]! : stoneT(u, v, l + 0.08)));
  blob(p.px, p.ax, p.ay - 50, 7, 3, M.stone, 0.65);
  return fin(p);
}

// ───────────────────────── banners and signs ─────────────────────────

/** A T-post with a long banner hung from its arm (≈ 80 px). */
function bannerT(cloth: Ramp, mark: Ramp): Sprite {
  const p = propPix(40, 100, 10, 92);
  box(p.px, p.P, [-0.2, -0.2, 0.2, 0.2], 0, 5, stoneT);
  pole(p.P, p, 0, 0, 0.06, 5, 82, M.wood);
  const [x, y] = p.P(0, 0, 80);
  p.px.rect(x - 2, y, 20, 2, M.wood[2]!);
  clothBanner(p.px, [x + 3, y + 2], 11, 50, cloth, mark, 0);
  tassel(p.px, [x + 17, y + 2], 8);
  return fin(p);
}

/** A tall pole with a swallow-tailed pennant (≈ 90 px). */
function pennant(cloth: Ramp): Sprite {
  const p = propPix(44, 104, 8, 98);
  pole(p.P, p, 0, 0, 0.05, 0, 90, M.lacquer);
  const [x, y] = p.P(0, 0, 88);
  for (let yy = 0; yy < 14; yy++) {
    const len = 28 - Math.abs(yy - 7) * (yy < 7 ? 0.4 : 0.4);
    const notch = Math.abs(yy - 7) < 3 ? 6 - Math.abs(yy - 7) * 2 : 0;
    for (let xx = 2; xx < len - notch; xx++) {
      const s = Math.round(Math.sin(xx / 5) * 1.5);
      p.px.set(x + xx, y + yy + s, snap(yy < 2 || yy > 11 ? M.gold : cloth, 0.5 + (xx / len) * 0.25, xx, yy, 0));
    }
  }
  p.px.rect(x - 1, y - 4, 3, 3, M.gold[3]!);
  return fin(p);
}

/** A sign frame: two posts with a hanging lacquered board between them (≈ 50 px). */
function signFrame(field: Ramp): Sprite {
  const p = propPix(60, 70, 30, 60);
  for (const x of [-0.7, 0.7]) pole(p.P, p, x, 0, 0.05, 0, 50);
  box(p.px, p.P, [-0.8, -0.05, 0.8, 0.05], 48, 51, wood(0.1));
  wall(p.px, p.P, 'left', 0.06, -0.55, 0.55, 26, 44, (u, v, l) => {
    if (Math.abs(u) > 8 || v < 27.2 || v > 42.8) return snap(M.gold, l + 0.2, u | 0, v | 0, 0);
    if (Math.abs(u) < 6 && (v | 0) % 5 !== 0 && hash2(u | 0, v | 0) > 0.55) return M.gold[3]!;
    return snap(field, l + 0.2, u | 0, v | 0, 0);
  });
  for (const x of [-0.45, 0.45]) {
    const [cx, cy] = p.P(x, 0.06, 48);
    p.px.rect(cx, cy, 1, 4, M.dark[2]!);
  }
  return fin(p);
}

/** A shop sign on a post: a vertical board with glyph strokes and a red tassel (≈ 56 px). */
function shopSign(field: Ramp): Sprite {
  const p = propPix(30, 76, 10, 68);
  pole(p.P, p, 0, 0, 0.06, 0, 58);
  box(p.px, p.P, [0, -0.04, 0.55, 0.04], 54, 57, wood(0.1));
  const [x, y] = p.P(0.45, 0, 54);
  for (let yy = 0; yy < 30; yy++)
    for (let xx = -3; xx <= 3; xx++) {
      const edge = Math.abs(xx) === 3 || yy < 1 || yy > 28;
      const glyph = Math.abs(xx) < 2 && yy > 3 && yy < 26 && (yy % 8 < 5) && hash2(xx + 9, yy) > 0.35;
      p.px.set(x + xx, y + 2 + yy, edge ? M.gold[3]! : glyph ? M.gold[4]! : snap(field, 0.55 + xx * 0.04, xx, yy, 0));
    }
  tassel(p.px, [x, y + 32], 7);
  return fin(p);
}

/** A string of small triangular flags between two posts (≈ 46 px). */
function flagString(): Sprite {
  const p = propPix(86, 64, 43, 56);
  for (const x of [-1.2, 1.2]) pole(p.P, p, x, 0, 0.05, 0, 44);
  const [ax, ay] = p.P(-1.2, 0, 42);
  const [bx, by] = p.P(1.2, 0, 42);
  const cols = [M.red, M.gold, K.indigo, M.jade, M.linen];
  for (let k = 0; k <= 60; k++) {
    const f = k / 60;
    const x = Math.round(ax + (bx - ax) * f);
    const y = Math.round(ay + (by - ay) * f + Math.sin(f * Math.PI) * 6);
    p.px.set(x, y, M.dark[2]!);
    if (k % 6 === 3) {
      const c = cols[(k / 6) % cols.length | 0]!;
      for (let t = 0; t < 6; t++) for (let w = -2 + Math.ceil(t / 2); w <= 2 - Math.ceil(t / 2); w++) p.px.set(x + w, y + 1 + t, snap(c, 0.65, w, t, 0));
    }
  }
  return fin(p);
}

// ───────────────────────── lanterns ─────────────────────────

/** A string of red lanterns between two posts (≈ 54 px). */
function lanternString(): Sprite {
  const p = propPix(96, 74, 48, 64);
  for (const x of [-1.4, 1.4]) pole(p.P, p, x, 0, 0.06, 0, 54, M.lacquer);
  const [ax, ay] = p.P(-1.4, 0, 52);
  const [bx, by] = p.P(1.4, 0, 52);
  for (let k = 0; k <= 60; k++) {
    const f = k / 60;
    p.px.set(ax + (bx - ax) * f, ay + (by - ay) * f + Math.sin(f * Math.PI) * 7, M.dark[2]!);
  }
  for (let k = 1; k <= 5; k++) {
    const f = k / 6;
    lantern(p.px, [ax + (bx - ax) * f, ay + (by - ay) * f + Math.sin(f * Math.PI) * 7], 2);
  }
  return fin(p);
}

/** A tall lantern pole with a crossbar and two lanterns (≈ 76 px). */
function lanternPole2(): Sprite {
  const p = propPix(44, 96, 22, 88);
  box(p.px, p.P, [-0.22, -0.22, 0.22, 0.22], 0, 6, stoneT);
  pole(p.P, p, 0, 0, 0.07, 6, 78, M.lacquer);
  box(p.px, p.P, [-0.6, -0.04, 0.6, 0.04], 72, 75, wood(0.1, K.maroon));
  lantern(p.px, p.P(-0.5, 0, 72), 3);
  lantern(p.px, p.P(0.5, 0, 72), 3);
  const [tx, ty] = p.P(0, 0, 78);
  p.px.rect(tx - 1, ty - 4, 3, 4, M.gold[3]!);
  return fin(p);
}

/** A six-sided palace lantern on a carved post (≈ 52 px): lit paper, gold frame, red tassels. */
function palaceLantern(): Sprite {
  const p = propPix(30, 70, 15, 62);
  box(p.px, p.P, [-0.2, -0.2, 0.2, 0.2], 0, 4, stoneT);
  pole(p.P, p, 0, 0, 0.06, 4, 34, K.maroon);
  const [x, y] = p.P(0, 0, 34);
  for (let b = 0; b < 16; b++) {
    const w = 6 - Math.abs(b - 8) * 0.25;
    for (let a = -Math.round(w); a <= Math.round(w); a++) {
      const frame = Math.abs(a) === Math.round(w) || a === 0 || b === 0 || b === 15;
      p.px.set(x + a, y - b, frame ? M.gold[2]! : snap(M.paper, 0.9 - Math.abs(a) * 0.06, a, b, 0));
    }
  }
  p.px.rect(x - 5, y - 18, 11, 2, K.maroon[3]!);
  p.px.rect(x - 2, y - 20, 5, 2, M.gold[3]!);
  tassel(p.px, [x - 6, y - 16], 7);
  tassel(p.px, [x + 6, y - 16], 7);
  return fin(p);
}

/** A tall pagoda stone lantern (≈ 64 px): stacked caps over a lit fire box. */
function pagodaLantern(): Sprite {
  const p = propPix(36, 84, 18, 76);
  box(p.px, p.P, [-0.38, -0.38, 0.38, 0.38], 0, 6, stoneT, stoneT);
  box(p.px, p.P, [-0.16, -0.16, 0.16, 0.16], 6, 26, stoneT);
  box(p.px, p.P, [-0.3, -0.3, 0.3, 0.3], 26, 30, stoneT, stoneT);
  box(p.px, p.P, [-0.22, -0.22, 0.22, 0.22], 30, 40, (u, v, l) => ((u | 0) % 5 > 1 && v > 31 && v < 39 ? snap(M.paper, 0.95, u | 0, v | 0, 0) : stoneT(u, v, l)));
  for (let k = 0; k < 3; k++) {
    const r = 0.42 - k * 0.1;
    box(p.px, p.P, [-r, -r, r, r], 40 + k * 7, 43 + k * 7, stoneT, stoneT);
    box(p.px, p.P, [-r * 0.5, -r * 0.5, r * 0.5, r * 0.5], 43 + k * 7, 47 + k * 7, stoneT);
  }
  blob(p.px, p.ax, p.ay - 63, 2, 3, M.stone, 0.7);
  return fin(p);
}

// ───────────────────────── fences and bridges ─────────────────────────

/** A bamboo fence, 2 tiles along x or y: green canes bound to two rails (≈ 26 px). */
function bambooFence(along: 'x' | 'y'): Sprite {
  const p = propPix(80, 56, 40, 34);
  for (let k = 0; k <= 16; k++) {
    const t = -1 + k / 8;
    const [x, y] = along === 'x' ? [t, 0] : [0, t];
    const [sx, sy] = p.P(x, y, 0);
    const h = 22 + (k % 3) * 2;
    for (let z = 0; z < h; z++) {
      p.px.set(sx, sy - z, z % 7 === 6 ? K.greenTile[2]! : M.jade[3 + (k % 2)]!);
      p.px.set(sx + 1, sy - z, M.jade[2]!);
    }
  }
  for (const z of [8, 17]) {
    const [ax, ay] = along === 'x' ? p.P(-1, 0, z) : p.P(0, -1, z);
    const [bx, by] = along === 'x' ? p.P(1, 0, z) : p.P(0, 1, z);
    for (let k = 0; k <= 40; k++) p.px.set(ax + (bx - ax) * (k / 40), ay + (by - ay) * (k / 40), K.straw[2]!);
  }
  return fin(p);
}

/** A white stone balustrade, 2 tiles along x or y (≈ 22 px). */
function stoneRail(along: 'x' | 'y'): Sprite {
  const p = propPix(80, 50, 40, 30);
  const r: [number, number, number, number] = along === 'x' ? [-1, -0.08, 1, 0.08] : [-0.08, -1, 0.08, 1];
  box(p.px, p.P, r, 0, 3, flat(M.linen, -0.1));
  box(p.px, p.P, r, 3, 15, (u, v, l) => (Math.round(u) % 8 < 2 ? snap(M.linen, l - 0.2, u | 0, v | 0, 0) : snap(M.linen, l + 0.05, u | 0, v | 0, 0)));
  box(p.px, p.P, r, 15, 18, flat(M.linen, 0.15));
  for (let k = 0; k <= 2; k++) {
    const t = -1 + k;
    const q: [number, number, number, number] = along === 'x' ? [t - 0.1, -0.1, t + 0.1, 0.1] : [-0.1, t - 0.1, 0.1, t + 0.1];
    box(p.px, p.P, q, 0, 22, flat(M.linen, 0.05));
    blob(p.px, ...p.P(along === 'x' ? t : 0, along === 'x' ? 0 : t, 24), 2, 2, M.linen, 0.75);
  }
  return fin(p);
}

/** A little humped stone footbridge, 4 tiles long along x, with rails (≈ 34 px). */
function archBridge(): Sprite {
  const p = propPix(150, 90, 75, 60);
  const N = 24;
  for (let k = 0; k < N; k++) {
    const t0 = -2 + (4 * k) / N;
    const t1 = -2 + (4 * (k + 1)) / N;
    const h0 = 4 + Math.sin(((t0 + 2) / 4) * Math.PI) * 14;
    box(p.px, p.P, [t0, -0.55, t1, 0.55], Math.max(0, h0 - 5), h0, (u, v, l) => snap(M.stone, l - 0.05 + ((u | 0) % 7 === 0 ? -0.2 : 0), u | 0, v | 0, 0), (u, v, l) => snap(M.stone, l + 0.08 + ((u | 0) % 6 === 0 ? -0.15 : 0), u | 0, v | 0, 0));
  }
  // The arch's side face with its dark opening.
  wall(p.px, p.P, 'left', 0.55, -2, 2, 0, 18, (u, v) => {
    const t = u / 16;
    const deck = 4 + Math.sin(((t + 2) / 4) * Math.PI) * 14 - 5;
    if (v > deck) return null;
    const arch = Math.sqrt(Math.max(0, 1 - (t / 1.4) ** 2)) * 11;
    if (v < arch) return Math.abs(t) < 1.4 ? (v > arch - 1.5 ? M.stone[4]! : M.blue[1]!) : null;
    return snap(M.stone, 0.45 + ((Math.floor(v / 4) + Math.floor(u / 7)) % 2 ? 0.05 : -0.05), u | 0, v | 0, 0);
  });
  for (const y of [-0.5, 0.5]) {
    for (let k = 0; k <= 8; k++) {
      const t = -2 + k / 2;
      const h = 4 + Math.sin(((t + 2) / 4) * Math.PI) * 14;
      box(p.px, p.P, [t - 0.07, y - 0.07, t + 0.07, y + 0.07], h, h + 10, flat(M.linen, 0.05));
    }
    for (let k = 0; k <= 40; k++) {
      const t = -2 + k / 10;
      const h = 4 + Math.sin(((t + 2) / 4) * Math.PI) * 14 + 8;
      const [x, yy] = p.P(t, y, h);
      p.px.rect(x, yy, 2, 2, M.linen[3]!);
    }
  }
  return fin(p);
}

/** A flat plank footbridge on posts, 4 tiles along x, with wooden rails (≈ 22 px). */
function plankBridge(): Sprite {
  const p = propPix(150, 70, 75, 44);
  for (const t of [-1.8, -0.6, 0.6, 1.8]) for (const y of [-0.5, 0.5]) pole(p.P, p, t, y, 0.06, -6, 6, M.wood);
  box(p.px, p.P, [-2, -0.6, 2, 0.6], 6, 9, wood(0), (u, v, l) => snap(M.deck, l + ((u | 0) % 5 === 0 ? -0.25 : 0.05), u | 0, v | 0, 0.2));
  for (const y of [-0.55, 0.55]) {
    for (let k = 0; k <= 4; k++) pole(p.P, p, -2 + k, y, 0.05, 9, 22, M.wood);
    box(p.px, p.P, [-2, y - 0.04, 2, y + 0.04], 20, 23, wood(0.1));
  }
  return fin(p);
}

// ───────────────────────── plants and rocks ─────────────────────────

/** A round flowering shrub (≈ 22 px): leaves in three tones with blossoms of `bloom`. */
function shrub(bloom: Ramp | null, seed: number): Sprite {
  const p = propPix(52, 44, 26, 38);
  const cx = p.ax;
  const cy = p.ay - 14;
  for (let b = -14; b <= 11; b++)
    for (let a = -20; a <= 20; a++) {
      const n = valueNoise((a + seed * 7) / 4, (b + seed) / 4);
      if ((a * a) / 400 + (b * b) / 196 > 0.75 + n * 0.35) continue;
      const lit = 0.45 + (a / 20) * 0.22 - (b / 14) * 0.25 + (n - 0.5) * 0.3;
      const flower = bloom && hash2(a * 7 + seed, b * 13) > 0.8 && b < 5;
      p.px.set(cx + a, cy + b, flower ? snap(bloom!, 0.6 + lit * 0.4, a, b, 0) : snap(M.moss, lit, cx + a, cy + b, 0.3));
    }
  return fin(p);
}

/** A stone planter box of flowers (≈ 18 px), 2 tiles along x. */
function flowerBed(bloom: Ramp): Sprite {
  const p = propPix(80, 44, 40, 30);
  box(p.px, p.P, [-1, -0.3, 1, 0.3], 0, 8, stoneT, (u, v) => snap(K.mud, 0.4, u | 0, v | 0, 0.3));
  for (let k = 0; k < 70; k++) {
    const [x, y] = p.P(-0.9 + hash2(k, 1) * 1.8, -0.25 + hash2(k, 2) * 0.5, 8);
    const h = 2 + Math.floor(hash2(k, 3) * 4);
    p.px.rect(x, y - h, 1, h, M.moss[2]!);
    if (k % 2) p.px.rect(x - 1, y - h - 1, 2, 2, snap(bloom, 0.5 + hash2(k, 4) * 0.4, k, 0, 0));
    else p.px.set(x + 1, y - h + 1, M.moss[2]!);
  }
  return fin(p);
}

/** A potted plant: a glazed pot with a small pine or a flowering stem (≈ 24 px). */
function pottedPlant(v: number): Sprite {
  const p = propPix(26, 40, 13, 34);
  jarAt(p, p.ax, p.ay, 9, 6, v ? K.indigo : M.terra, true);
  if (v) {
    for (let k = 0; k < 18; k++) p.px.set(p.ax + Math.round(Math.sin(k / 4) * 3), p.ay - 10 - k, M.moss[2]!);
    for (let k = 0; k < 6; k++) blob(p.px, p.ax + Math.round(Math.sin(k) * 5), p.ay - 14 - k * 3, 2, 1.5, M.red, 0.7);
  } else {
    p.px.rect(p.ax, p.ay - 20, 1, 10, M.wood[2]!);
    blob(p.px, p.ax - 4, p.ay - 18, 5, 2, M.moss, 0.45);
    blob(p.px, p.ax + 3, p.ay - 22, 5, 2, M.moss, 0.55);
  }
  return fin(p);
}

/** A clump of bamboo with a few stones at its foot (≈ 90 px). */
function bambooClump(seed: number): Sprite {
  const p = propPix(60, 110, 30, 102);
  for (let k = 0; k < 9; k++) {
    const x = p.ax + Math.round((hash2(k, seed) - 0.5) * 30);
    const y = p.ay - Math.round(hash2(k, seed + 5) * 6);
    const h = 60 + Math.floor(hash2(k, seed + 9) * 34);
    const lean = (hash2(k, seed + 2) - 0.5) * 0.15;
    for (let z = 0; z < h; z++) {
      const xx = Math.round(x + z * lean);
      const node = z % 11 === 10;
      p.px.set(xx, y - z, node ? M.jade[1]! : M.jade[3]!);
      p.px.set(xx + 1, y - z, node ? M.jade[1]! : M.jade[k % 2 ? 4 : 2]!);
      if (z > h * 0.45 && z % 9 === 4) {
        const dir = (z + k) % 2 ? 1 : -1;
        for (let t = 0; t < 6; t++) {
          p.px.set(xx + dir * (2 + t), y - z + Math.round(t * 0.5), snap(M.moss, 0.55 + t * 0.03, t, z, 0));
          p.px.set(xx + dir * (2 + t), y - z + Math.round(t * 0.5) + 1, snap(M.moss, 0.35, t, z, 0));
        }
      }
    }
  }
  blob(p.px, p.ax - 8, p.ay - 2, 5, 3, M.stone, 0.5);
  blob(p.px, p.ax + 9, p.ay - 1, 4, 2, M.stone, 0.55);
  return fin(p);
}

/** A pile of cut bamboo poles (≈ 14 px). */
function bambooPile(): Sprite {
  const p = propPix(70, 34, 35, 24);
  for (let row = 0; row < 3; row++)
    for (let k = 0; k < 5 - row; k++) {
      const [ax, ay] = p.P(-1, -0.3 + k * 0.14 + row * 0.07, 2 + row * 4);
      const [bx, by] = p.P(1, -0.3 + k * 0.14 + row * 0.07, 2 + row * 4);
      for (let t = 0; t <= 40; t++) {
        const x = ax + (bx - ax) * (t / 40);
        const y = ay + (by - ay) * (t / 40);
        p.px.set(x, y, M.jade[3]!);
        p.px.set(x, y + 1, M.jade[2]!);
      }
      p.px.rect(bx - 1, by - 1, 3, 3, K.straw[4]!);
    }
  return fin(p);
}

/** A cluster of mossy boulders (≈ 26 px). */
function rockCluster(seed: number): Sprite {
  const p = propPix(64, 44, 32, 36);
  for (let k = 0; k < 5; k++) {
    const x = p.ax + Math.round((hash2(k, seed) - 0.5) * 34);
    const y = p.ay - 4 - Math.round(hash2(k, seed + 1) * 6);
    const r = 5 + hash2(k, seed + 2) * 7;
    for (let b = -Math.round(r); b <= Math.round(r * 0.6); b++)
      for (let a = -Math.round(r * 1.2); a <= Math.round(r * 1.2); a++) {
        const n = valueNoise((a + k * 13) / 3, (b + seed) / 3);
        if ((a * a) / (r * r * 1.44) + (b * b) / (r * r) > 0.8 + n * 0.3) continue;
        const top = b < -r * 0.45;
        const lit = 0.45 + (a / r) * 0.18 - (b / r) * 0.22 + (n - 0.5) * 0.25;
        p.px.set(x + a, y + b, top && n > 0.55 ? snap(M.moss, lit, a, b, 0.3) : snap(M.stone, lit, x + a, y + b, 0.3));
      }
  }
  return fin(p);
}

/** A tall scholar's rock (太湖石-like): a holed, leaning pillar on a plinth (≈ 64 px). */
function rockPillar(): Sprite {
  const p = propPix(40, 84, 20, 76);
  box(p.px, p.P, [-0.4, -0.3, 0.4, 0.3], 0, 5, stoneT, stoneT);
  for (let z = 0; z < 60; z++) {
    const w = 6 + Math.sin(z / 7) * 3 + (z > 50 ? (z - 50) * 0.4 : 0);
    const cx = p.ax + Math.round(Math.sin(z / 13) * 4);
    for (let a = -Math.round(w); a <= Math.round(w); a++) {
      const hole = valueNoise((a + 20) / 2.5, z / 3.5) > 0.72;
      const y = p.ay - 5 - z;
      if (hole) {
        p.px.set(cx + a, y, M.dark[1]!);
        continue;
      }
      p.px.set(cx + a, y, snap(M.stone, 0.45 + (a / w) * 0.25 + (valueNoise(a / 3, z / 5) - 0.5) * 0.3, a, z, 0.3));
    }
  }
  return fin(p);
}

/** A chunk of cliff with a mossy top and a fern (≈ 70 px). */
function cliffChunk(): Sprite {
  const p = propPix(80, 96, 40, 82);
  box(p.px, p.P, [-1, -0.7, 1, 0.7], 0, 62, (u, v, l) => {
    const n = valueNoise(u / 6, v / 9);
    const crack = Math.abs(valueNoise(u / 4 + 9, v / 20) - 0.5) < 0.03;
    return crack ? M.dark[1]! : snap(M.stone, l - 0.1 + (n - 0.5) * 0.35 + ((v | 0) % 13 === 0 ? -0.15 : 0), u | 0, v | 0, 0.3);
  }, (u, v, l) => (valueNoise(u / 5, v / 5) > 0.35 ? snap(M.moss, l - 0.2 + valueNoise(u / 2, v / 2) * 0.3, u | 0, v | 0, 0.3) : stoneT(u, v, l)));
  for (let k = 0; k < 12; k++) {
    const [x, y] = p.P(-0.8 + hash2(k, 7) * 1.6, 0.7, 10 + hash2(k, 8) * 48);
    p.px.rect(x, y, 2, 1, M.moss[2]!);
  }
  return fin(p);
}

/** Lily pads and lotus flowers on water (flat). */
function lilyPads(seed: number): Sprite {
  const p = propPix(70, 36, 35, 22);
  for (let k = 0; k < 9; k++) {
    const [x, y] = p.P((hash2(k, seed) - 0.5) * 2, (hash2(k, seed + 3) - 0.5) * 1.2, 0);
    const r = 3 + Math.floor(hash2(k, seed + 6) * 3);
    for (let b = -r; b <= r; b++)
      for (let a = -r * 2; a <= r * 2; a++) {
        if ((a * a) / (4 * r * r) + (b * b) / (r * r) > 1) continue;
        if (a > 0 && Math.abs(b) < 1) continue; // the notch
        p.px.set(x + a, y + b * 0.5, snap(M.moss, 0.45 + (a / (2 * r)) * 0.2 - b * 0.03, a, b, 0));
      }
    if (k % 3 === 0) {
      blob(p.px, x - 1, y - 3, 2.5, 2, K.wallPlaster, 0.85);
      p.px.set(x - 1, y - 5, M.red[4]!);
      p.px.set(x, y - 4, M.red[3]!);
    }
  }
  return fin(p);
}

/** A garden cascade: a mossy rock with a thin fall of water into a small pool (≈ 50 px; the water drawn still). */
function cascade(): Sprite {
  const p = propPix(70, 76, 35, 64);
  blob(p.px, p.ax, p.ay - 4, 24, 6, M.blue, 0.55);
  blob(p.px, p.ax, p.ay - 4, 18, 4, M.blue, 0.75);
  for (let z = 0; z < 46; z++) {
    const w = 12 - z * 0.12;
    for (let a = -Math.round(w); a <= Math.round(w); a++) {
      if (a > -3 && a < 3 && z < 38) continue;
      p.px.set(p.ax + a - 6, p.ay - 8 - z, snap(z > 36 ? M.moss : M.stone, 0.4 + a * 0.02 + (valueNoise(a / 3, z / 4) - 0.5) * 0.3, a, z, 0.3));
    }
  }
  for (let z = 0; z < 38; z++) for (let a = -2; a <= 2; a++) p.px.set(p.ax + a - 6, p.ay - 8 - z, (z + a * 3) % 5 === 0 ? M.linen[4]! : M.blue[3 + (a & 1)]!);
  for (let k = 0; k < 8; k++) p.px.set(p.ax - 6 + Math.round((hash2(k, 4) - 0.5) * 12), p.ay - 8 + Math.round(hash2(k, 5) * 3), M.linen[4]!);
  return fin(p);
}

/** A stone basin of water on a short pedestal (≈ 20 px). */
function stoneBasin(): Sprite {
  const p = propPix(34, 34, 17, 26);
  box(p.px, p.P, [-0.15, -0.15, 0.15, 0.15], 0, 8, stoneT);
  blob(p.px, p.ax, p.ay - 12, 11, 5, M.stone, 0.55);
  blob(p.px, p.ax, p.ay - 14, 8, 3, M.blue, 0.55);
  p.px.rect(p.ax - 2, p.ay - 15, 3, 1, M.linen[4]!);
  return fin(p);
}

/** A small stone stupa (≈ 48 px). */
function stupa(): Sprite {
  const p = propPix(36, 66, 18, 58);
  box(p.px, p.P, [-0.45, -0.45, 0.45, 0.45], 0, 6, stoneT, stoneT);
  box(p.px, p.P, [-0.3, -0.3, 0.3, 0.3], 6, 12, stoneT, stoneT);
  blob(p.px, p.ax, p.ay - 22, 9, 10, M.stone, 0.55);
  box(p.px, p.P, [-0.12, -0.12, 0.12, 0.12], 30, 34, stoneT);
  for (let k = 0; k < 5; k++) p.px.rect(p.ax - 3 + (k >> 1), p.ay - 36 - k * 3, 7 - (k >> 1) * 2, 2, M.stone[3 - (k & 1)]!);
  blob(p.px, p.ax, p.ay - 52, 2, 2, M.gold, 0.7);
  return fin(p);
}

// ───────────────────────── market, carts, furniture ─────────────────────────

/** Baskets of produce on the ground: a mix of fruit, greens and roots (≈ 14 px). */
function produce(seed: number): Sprite {
  const p = propPix(56, 30, 28, 22);
  for (let k = 0; k < 4; k++) {
    const [x, y] = p.P(-0.6 + (k % 2) * 0.9, -0.2 + (k >> 1) * 0.45, 0);
    blob(p.px, x, y - 4, 6, 4, K.straw, 0.45);
    const c = [M.red, M.jade, K.ochre, M.terra, M.moss][(k + seed) % 5]!;
    for (let q = 0; q < 9; q++) blob(p.px, x - 4 + (q % 3) * 4, y - 8 + Math.floor(q / 3) * 1.5, 1.8, 1.4, c, 0.6 + hash2(q, k) * 0.2);
  }
  return fin(p);
}

/** A rack with fish and meats hanging (≈ 40 px). */
function hangingRack(): Sprite {
  const p = propPix(56, 60, 28, 52);
  for (const x of [-0.8, 0.8]) pole(p.P, p, x, 0, 0.05, 0, 40);
  box(p.px, p.P, [-0.85, -0.04, 0.85, 0.04], 38, 41, wood(0.1));
  for (let k = 0; k < 6; k++) {
    const [x, y] = p.P(-0.6 + k * 0.24, 0, 38);
    p.px.rect(x, y, 1, 3, M.dark[2]!);
    const fish = k % 2 === 0;
    for (let t = 0; t < 12; t++) {
      const w = fish ? (t < 9 ? 2 : t === 11 ? 3 : 1) : 2 + (t < 6 ? 1 : 0);
      for (let a = -w + 1; a < w; a++) p.px.set(x + a, y + 3 + t, fish ? snap(K.iron, 0.6 + a * 0.1, a, t, 0) : snap(M.red, 0.45 + a * 0.1, a, t, 0));
    }
  }
  return fin(p);
}

/** A stack of pots and bowls (≈ 22 px). */
function pottery(): Sprite {
  const p = propPix(50, 36, 25, 28);
  jarAt(p, p.ax - 10, p.ay, 14, 6, M.terra, true);
  jarAt(p, p.ax + 2, p.ay - 1, 10, 5, K.indigo, true);
  jarAt(p, p.ax + 12, p.ay, 7, 5, M.terra);
  blob(p.px, p.ax + 4, p.ay + 1, 6, 2, M.linen, 0.6);
  return fin(p);
}

/** A wheelbarrow with a sack (≈ 18 px). */
function wheelbarrow(): Sprite {
  const p = propPix(56, 40, 28, 30);
  box(p.px, p.P, [-0.5, -0.25, 0.3, 0.25], 8, 14, wood(0), wood(0.1));
  const [wx, wy] = p.P(0.45, 0, 6);
  for (let b = -6; b <= 6; b++) for (let a = -3; a <= 3; a++) if ((a * a) / 9 + (b * b) / 36 <= 1 && ((a * a) / 9 + (b * b) / 36 > 0.5 || a === 0)) p.px.set(wx + a, wy + b, M.wood[2]!);
  const [hx, hy] = p.P(-0.5, 0, 12);
  for (let k = 0; k < 14; k++) p.px.rect(hx - k, hy + Math.round(k * 0.45), 1, 2, M.wood[3]!);
  blob(p.px, ...p.P(-0.1, 0, 18), 6, 4, K.cream, 0.55);
  return fin(p);
}

/** A covered wagon: a cart with a hooped cloth canopy and big spoked wheels (≈ 46 px). */
function coveredWagon(): Sprite {
  const p = propPix(96, 74, 48, 60);
  box(p.px, p.P, [-1.2, -0.5, 1.2, 0.5], 12, 18, wood(0), wood(0.1));
  for (let k = 0; k <= 12; k++) {
    const t = k / 12;
    const yy = -0.5 + t;
    const h = Math.sin(t * Math.PI) * 18;
    const [ax, ay] = p.P(-1.1, yy, 18 + h);
    const [bx, by] = p.P(1.0, yy, 18 + h);
    for (let q = 0; q <= 30; q++) {
      const x = ax + (bx - ax) * (q / 30);
      const y = ay + (by - ay) * (q / 30);
      p.px.set(x, y, snap(K.cream, 0.4 + t * 0.4 + (q % 10 === 0 ? -0.2 : 0), q, k, 0));
      p.px.set(x, y + 1, snap(K.cream, 0.35 + t * 0.4, q, k, 0));
    }
  }
  for (const [x, y] of [
    [-0.4, 0.55],
    [0.7, 0.55],
  ] as const) {
    const [cx, cy] = p.P(x, y, 10);
    for (let b = -10; b <= 10; b++)
      for (let a = -5; a <= 5; a++) {
        const e = (a * a) / 25 + (b * b) / 100;
        if (e <= 1 && (e > 0.65 || a === 0 || b === 0 || Math.abs(a * 2 - b) < 1)) p.px.set(cx + a, cy + b, M.wood[e > 0.8 ? 1 : 3]!);
      }
  }
  const [sx, sy] = p.P(1.2, 0, 14);
  for (let k = 0; k < 20; k++) p.px.rect(sx + k, sy + Math.round(k * 0.5), 1, 2, M.wood[2]!);
  return fin(p);
}

/** A tea table with two stools and a pot and cups (≈ 18 px). */
function teaTable(): Sprite {
  const p = propPix(60, 40, 30, 30);
  for (const [x, y] of [
    [-0.7, 0.3],
    [0.3, 0.7],
  ] as const)
    box(p.px, p.P, [x - 0.15, y - 0.15, x + 0.15, y + 0.15], 0, 8, wood(-0.05), wood(0.15));
  for (const [x, y] of [
    [-0.35, -0.3],
    [0.35, -0.3],
    [-0.35, 0.3],
    [0.35, 0.3],
  ] as const)
    pole(p.P, p, x, y, 0.04, 0, 12);
  box(p.px, p.P, [-0.45, -0.4, 0.45, 0.4], 12, 15, wood(0.05, K.maroon), wood(0.2, K.maroon));
  jarAt(p, ...p.P(0, 0, 15), 5, 3, M.linen, true);
  for (const x of [-0.25, 0.2]) blob(p.px, ...p.P(x, 0.2, 16), 1.5, 1, M.linen, 0.8);
  return fin(p);
}

/** A shelf of jars and bowls (≈ 40 px). */
function shelf(): Sprite {
  const p = propPix(56, 60, 28, 52);
  box(p.px, p.P, [-0.8, -0.15, 0.8, 0.15], 0, 42, (u, v, l) => ((v | 0) % 13 < 2 || Math.abs(u) > 11.5 ? snap(M.wood, l, u | 0, v | 0) : snap(M.dark, 0.3, u | 0, v | 0)), wood(0.15));
  for (let row = 0; row < 3; row++)
    for (let k = 0; k < 4; k++) {
      const [x, y] = p.P(-0.55 + k * 0.36, 0.16, 2 + row * 13);
      jarAt(p, x, y, 6 + (k % 2) * 2, 2.5, [M.terra, K.indigo, M.linen, M.jade][(k + row) % 4]!, true);
    }
  return fin(p);
}

/** A pair of blue-and-white vases (≈ 22 px). */
function vases(): Sprite {
  const p = propPix(40, 36, 20, 30);
  for (const [dx, h] of [
    [-6, 20],
    [7, 15],
  ] as const) {
    jarAt(p, p.ax + dx, p.ay, h, 5, M.linen, true);
    for (let b = 3; b < h - 2; b += 3) for (let a = -3; a <= 3; a++) if ((a + b) % 3 === 0) p.px.set(p.ax + dx + a, p.ay - b, K.indigo[2]!);
  }
  return fin(p);
}

// ───────────────────────── training ─────────────────────────

/** An archery target on a stand: straw boss with red and white rings (≈ 40 px). */
function target(): Sprite {
  const p = propPix(40, 56, 20, 48);
  for (const x of [-0.35, 0.35]) pole(p.P, p, x, 0.2, 0.04, 0, 30);
  const [cx, cy] = p.P(0, 0, 26);
  for (let b = -12; b <= 12; b++)
    for (let a = -11; a <= 11; a++) {
      const d = Math.sqrt((a * a) / 121 + (b * b) / 144);
      if (d > 1) continue;
      const ring = Math.floor(d * 5);
      const c = ring === 0 ? M.red[3]! : ring === 1 ? K.cream[4]! : ring === 2 ? M.red[2]! : ring === 3 ? K.cream[3]! : K.straw[3]!;
      p.px.set(cx + a, cy + b, c);
    }
  p.px.rect(cx + 3, cy - 4, 6, 1, M.wood[4]!);
  p.px.rect(cx + 9, cy - 5, 2, 3, M.linen[4]!);
  return fin(p);
}

/** A big war drum on a red stand (≈ 44 px). */
function warDrum(): Sprite {
  const p = propPix(52, 60, 26, 52);
  for (const [x, y] of [
    [-0.45, -0.2],
    [0.45, -0.2],
    [-0.45, 0.2],
    [0.45, 0.2],
  ] as const)
    pole(p.P, p, x, y, 0.05, 0, 20, M.lacquer);
  const [cx, cy] = p.P(0, 0, 32);
  for (let b = -14; b <= 14; b++)
    for (let a = -12; a <= 12; a++) {
      const e = (a * a) / 144 + (b * b) / 196;
      if (e > 1) continue;
      const head = (a + 3) * (a + 3) / 64 + (b * b) / 156 <= 1 && a > -6;
      const stud = Math.abs(e - 0.82) < 0.05 && (b + 20) % 4 === 0;
      p.px.set(cx + a, cy + b, stud ? M.gold[4]! : head ? snap(K.cream, 0.55 + a * 0.03, a, b, 0) : snap(M.red, 0.45 + a * 0.02, a, b, 0));
    }
  p.px.rect(cx - 16, cy - 16, 2, 22, M.wood[3]!);
  return fin(p);
}

/** Plum-blossom posts (梅花樁): five standing logs of different heights (≈ 42 px). */
function plumPosts(): Sprite {
  const p = propPix(90, 74, 45, 56);
  const pts: [number, number, number][] = [
    [0, -1, 26],
    [-1, 0, 34],
    [1, 0, 30],
    [0, 1, 22],
    [0, 0, 40],
  ];
  for (const [x, y, h] of pts.sort((a, b) => a[0] + a[1] - b[0] - b[1])) {
    box(p.px, p.P, [x - 0.11, y - 0.11, x + 0.11, y + 0.11], 0, h, (u, v, l) => snap(M.wood, l + ((v | 0) % 7 === 0 ? -0.15 : 0), u | 0, v | 0), (u, v) => snap(M.deck, 0.7 - (Math.abs(u % 4) < 1 ? 0.2 : 0), u | 0, v | 0, 0));
  }
  return fin(p);
}

/** Stone lifting weights (石鎖) and a stone roller (≈ 12 px). */
function stoneWeights(): Sprite {
  const p = propPix(50, 30, 25, 22);
  for (const x of [-0.5, 0.1]) {
    box(p.px, p.P, [x - 0.2, -0.15, x + 0.2, 0.15], 0, 9, stoneT, stoneT);
    const [hx, hy] = p.P(x, 0, 9);
    p.px.rect(hx - 3, hy - 4, 7, 1, M.stone[2]!);
    p.px.rect(hx - 3, hy - 4, 1, 4, M.stone[2]!);
    p.px.rect(hx + 3, hy - 4, 1, 4, M.stone[2]!);
  }
  const [rx, ry] = p.P(0.6, 0.3, 5);
  for (let b = -5; b <= 5; b++) for (let a = -7; a <= 7; a++) if (Math.abs(b) <= 5 - Math.abs(a) * 0.1) p.px.set(rx + a, ry + b, snap(M.stone, 0.5 - b * 0.04 + a * 0.02, a, b, 0));
  return fin(p);
}

// ───────────────────────── shrines ─────────────────────────

/** An altar table with candles, an incense pot and offerings of fruit (≈ 30 px). */
function altar(): Sprite {
  const p = propPix(60, 50, 30, 40);
  box(p.px, p.P, [-0.8, -0.3, 0.8, 0.3], 0, 16, (u, v, l) => (v > 12 ? snap(M.gold, l, u | 0, v | 0, 0) : snap(M.red, l + 0.1, u | 0, v | 0)), wood(0.1, K.maroon));
  for (const x of [-0.6, 0.6]) {
    const [cx, cy] = p.P(x, 0, 16);
    p.px.rect(cx, cy - 8, 2, 8, M.red[3]!);
    p.px.set(cx, cy - 9, M.gold[4]!);
    p.px.set(cx, cy - 10, M.paper[4]!);
  }
  const [ix, iy] = p.P(0, 0, 16);
  blob(p.px, ix, iy - 3, 4, 3, M.gold, 0.55);
  for (let k = -1; k <= 1; k++) p.px.rect(ix + k * 2, iy - 11, 1, 6, M.red[2]!);
  for (const x of [-0.3, 0.3]) {
    const [fx, fy] = p.P(x, 0.15, 16);
    blob(p.px, fx, fy - 1, 3, 1, M.linen, 0.7);
    for (let q = 0; q < 3; q++) blob(p.px, fx - 2 + q * 2, fy - 3, 1.4, 1.2, x < 0 ? M.red : K.ochre, 0.7);
  }
  return fin(p);
}

/** A wayside shrine on a post (土地 niche): a tiny roofed box with a lit tablet (≈ 40 px). */
function roadShrine(): Sprite {
  const p = propPix(36, 60, 18, 52);
  box(p.px, p.P, [-0.3, -0.3, 0.3, 0.3], 0, 14, stoneT, stoneT);
  box(p.px, p.P, [-0.25, -0.25, 0.25, 0.25], 14, 30, (u, v, l) => (Math.abs(u) < 3 && v > 16 && v < 27 ? (v > 24 ? M.gold[3]! : M.red[2]!) : snap(M.lacquer, l, u | 0, v | 0)));
  box(p.px, p.P, [-0.38, -0.38, 0.38, 0.38], 30, 33, tiles(K.greenTile), tiles(K.greenTile));
  blob(p.px, p.ax, p.ay - 38, 5, 3, K.greenTile, 0.6);
  p.px.set(p.ax, p.ay - 42, M.gold[4]!);
  return fin(p);
}

// ───────────────────────── small decor, cloth, docks, stacks ─────────────────────────

/** A cluster of wine jars sealed with red cloth (≈ 18 px). */
function wineJars(): Sprite {
  const p = propPix(50, 34, 25, 26);
  for (const [dx, dy] of [
    [-9, -2],
    [3, -3],
    [-3, 2],
    [10, 1],
  ] as const) {
    jarAt(p, p.ax + dx, p.ay + dy, 12, 5, M.terra, true);
    blob(p.px, p.ax + dx, p.ay + dy - 13, 4, 2, M.red, 0.6);
  }
  return fin(p);
}

/** Baskets, round and square, some stacked (≈ 18 px). */
function baskets(): Sprite {
  const p = propPix(50, 34, 25, 26);
  for (const [dx, dy, h] of [
    [-8, 0, 8],
    [-8, -8, 7],
    [6, 1, 9],
  ] as const) {
    for (let b = 0; b < h; b++)
      for (let a = -6; a <= 6; a++) p.px.set(p.ax + dx + a, p.ay + dy - b, snap(K.straw, 0.45 + a * 0.03 + ((a + b) % 2 ? 0.08 : -0.08), a, b, 0));
    blob(p.px, p.ax + dx, p.ay + dy - h, 6, 2, K.straw, 0.3);
  }
  return fin(p);
}

/** An open oil-paper umbrella standing on its handle (≈ 30 px). */
function umbrella(cloth: Ramp): Sprite {
  const p = propPix(44, 44, 22, 38);
  p.px.rect(p.ax, p.ay - 20, 1, 20, M.wood[2]!);
  for (let b = 0; b < 10; b++) {
    const w = 4 + b * 1.8;
    for (let a = -Math.round(w); a <= Math.round(w); a++) {
      const rib = Math.round((Math.atan2(b + 1, a) / Math.PI) * 14) % 2 === 0;
      p.px.set(p.ax + a, p.ay - 30 + b, snap(cloth, 0.45 + (a / w) * 0.25 + (rib ? 0.1 : 0) + (b === 9 ? -0.2 : 0), a, b, 0));
    }
  }
  p.px.set(p.ax, p.ay - 31, M.gold[3]!);
  return fin(p);
}

/** A bird cage on a stand (≈ 40 px). */
function birdCage(): Sprite {
  const p = propPix(30, 56, 15, 48);
  pole(p.P, p, 0, 0, 0.04, 0, 26);
  const [x, y] = p.P(0, 0, 26);
  for (let b = 0; b < 14; b++) {
    const w = b < 3 ? b * 2 : 6;
    for (let a = -w; a <= w; a++) if (Math.abs(a) === w || a % 3 === 0 || b === 13) p.px.set(x + a, y - 14 + b, M.deck[3]!);
  }
  p.px.rect(x - 1, y - 7, 2, 2, K.ochre[4]!);
  p.px.set(x, y - 16, M.gold[3]!);
  return fin(p);
}

/** A cloth canopy on four poles, sloped (≈ 46 px) – a market tent. */
function canopy(cloth: Ramp): Sprite {
  const p = propPix(80, 70, 40, 54);
  for (const [x, y] of [
    [-1, -0.8],
    [1, -0.8],
    [-1, 0.8],
    [1, 0.8],
  ] as const)
    pole(p.P, p, x, y, 0.04, 0, y < 0 ? 46 : 38, M.wood);
  const pts = [p.P(-1.15, 0.95, 37), p.P(1.15, 0.95, 37), p.P(1.15, -0.95, 47), p.P(-1.15, -0.95, 47)];
  const xs = pts.map((q) => q[0]);
  const ys = pts.map((q) => q[1]);
  for (let y = Math.floor(Math.min(...ys)); y <= Math.max(...ys); y++)
    for (let x = Math.floor(Math.min(...xs)); x <= Math.max(...xs); x++) {
      let inside = false;
      for (let i = 0, j = 3; i < 4; j = i++) {
        const [xi, yi] = pts[i]!;
        const [xj, yj] = pts[j]!;
        if (yi > y + 0.5 !== yj > y + 0.5 && x + 0.5 < ((xj - xi) * (y + 0.5 - yi)) / (yj - yi) + xi) inside = !inside;
      }
      if (inside) p.px.set(x, y, snap(cloth, 0.55 + (Math.floor((x * 0.5 + y) / 5) % 2 ? 0.08 : -0.08), x, y, 0));
    }
  for (let k = 0; k <= 40; k++) {
    const [x, y] = p.P(-1.15 + 2.3 * (k / 40), 0.95, 37);
    p.px.rect(x, y, 1, 2 + (k % 4 < 2 ? 1 : 0), cloth[1]!);
  }
  return fin(p);
}

/** Long dyed cloths hung on a high frame to dry (≈ 64 px; the dye works). */
function dyeRack(): Sprite {
  const p = propPix(90, 84, 45, 76);
  for (const x of [-1.3, 1.3]) pole(p.P, p, x, 0, 0.05, 0, 66);
  box(p.px, p.P, [-1.35, -0.04, 1.35, 0.04], 64, 67, wood(0.1));
  const cols = [K.indigo, M.red, K.ochre, M.jade, K.indigo];
  for (let k = 0; k < 5; k++) {
    const [x, y] = p.P(-1.1 + k * 0.55, 0, 64);
    for (let yy = 0; yy < 50 - (k % 2) * 8; yy++)
      for (let xx = 0; xx < 7; xx++) p.px.set(x + xx, y + yy + Math.round(xx * 0.5), snap(cols[k]!, 0.4 + xx * 0.05 + (yy % 9 === 0 ? -0.1 : 0), xx, yy, 0));
  }
  return fin(p);
}

/** A dock section on posts, 2 tiles along x, with a mooring post and its lantern (≈ 40 px). */
function dock(): Sprite {
  const p = propPix(90, 70, 45, 48);
  for (const x of [-0.9, 0, 0.9]) for (const y of [-0.5, 0.5]) pole(p.P, p, x, y, 0.06, -10, 6, K.mud);
  box(p.px, p.P, [-1, -0.6, 1, 0.6], 6, 9, wood(0), (u, v, l) => snap(M.deck, l + ((u | 0) % 5 === 0 ? -0.25 : 0.05) + (hash2((u / 5) | 0, 7) - 0.5) * 0.1, u | 0, v | 0, 0.2));
  pole(p.P, p, 0.9, 0.5, 0.07, 9, 40, M.wood);
  box(p.px, p.P, [0.9, 0.45, 1.25, 0.55], 36, 38, wood(0.1));
  lantern(p.px, p.P(1.2, 0.5, 36), 2);
  const [rx, ry] = p.P(0.9, 0.5, 14);
  for (let k = 0; k < 6; k++) p.px.rect(rx - 2, ry - k, 5, 1, k % 2 ? K.straw[2]! : K.straw[4]!);
  return fin(p);
}

/** A crate stack (≈ 30 px). */
function crateStack(): Sprite {
  const p = propPix(60, 50, 30, 40);
  const crate = (x0: number, y0: number, z0: number, s = 0.32) =>
    box(p.px, p.P, [x0 - s, y0 - s, x0 + s, y0 + s], z0, z0 + 11, (u, v, l) => snap(M.deck, l + ((u | 0) % 10 < 1 || (v | 0) % 10 < 1 || Math.abs(((u | 0) % 10) - ((v | 0) % 10)) < 1 ? -0.25 : 0), u | 0, v | 0, 0), flat(M.deck, 0.15));
  crate(-0.4, 0, 0);
  crate(0.3, 0.1, 0);
  crate(-0.1, 0.0, 11, 0.3);
  return fin(p);
}

/** A stack of grain sacks tied at the neck (≈ 22 px). */
function sackStack(): Sprite {
  const p = propPix(56, 40, 28, 32);
  for (const [dx, dy] of [
    [-9, 0],
    [3, 1],
    [-3, -7],
    [13, 2],
  ] as const) {
    blob(p.px, p.ax + dx, p.ay + dy - 6, 7, 5, K.cream, 0.5);
    p.px.rect(p.ax + dx - 1, p.ay + dy - 12, 3, 2, K.straw[2]!);
  }
  return fin(p);
}

/** Barrels, three on the ground and one on top (≈ 28 px). */
function barrelStack(): Sprite {
  const p = propPix(56, 44, 28, 36);
  barrelAt(p, p.ax - 10, p.ay - 1);
  barrelAt(p, p.ax + 1, p.ay + 1);
  barrelAt(p, p.ax + 12, p.ay);
  barrelAt(p, p.ax - 4, p.ay - 15, 13);
  return fin(p);
}

/** Seasonal ground bits (flat): fallen leaves, fallen petals, a puddle, snow. */
function groundBits(kind: 'leaves' | 'petals' | 'puddle' | 'snow'): Sprite {
  const p = propPix(64, 30, 32, 16);
  if (kind === 'puddle') {
    for (let b = -4; b <= 4; b++)
      for (let a = -16; a <= 16; a++)
        if ((a * a) / 256 + (b * b) / 16 <= 1 - valueNoise(a / 4, b / 2) * 0.3) p.px.set(p.ax + a, p.ay - 4 + b, (a - b * 3) % 9 === 0 ? M.linen[3]! : M.blue[2]!);
    return fin(p);
  }
  if (kind === 'snow') {
    blob(p.px, p.ax - 6, p.ay - 4, 12, 4, M.linen, 0.75);
    blob(p.px, p.ax + 8, p.ay - 3, 8, 3, M.linen, 0.85);
    return fin(p);
  }
  const cols = kind === 'leaves' ? [M.red, K.ochre, M.terra] : [M.red, M.linen];
  for (let k = 0; k < 26; k++) {
    const x = p.ax + Math.round((hash2(k, 1) - 0.5) * 50);
    const y = p.ay - 4 + Math.round((hash2(k, 2) - 0.5) * 10);
    const c = cols[k % cols.length]!;
    if (kind === 'petals') p.px.set(x, y, k % 2 ? snap(M.red, 0.85, k, 0, 0) : K.wallPlaster[5]!);
    else p.px.rect(x, y, 2, 1, snap(c, 0.5 + hash2(k, 3) * 0.3, k, 0, 0));
  }
  return fin(p);
}


// ───────────────────────── round 2b (biomes-kit-sheet-3: temple, wall, training, arena) ─────────────────────────

/** A stone guardian lion seated on a plinth, paw on a ball (≈ 46 px). */
function stoneLion(flip: boolean): Sprite {
  const p = propPix(40, 64, 20, 56);
  box(p.px, p.P, [-0.4, -0.3, 0.4, 0.3], 0, 14, stoneT, stoneT);
  const s = flip ? -1 : 1;
  const c = p.ax;
  const b = p.ay - 14;
  blob(p.px, c - s * 2, b - 9, 7, 9, M.stone, 0.55); // body
  blob(p.px, c + s * 2, b - 22, 7, 6, M.stone, 0.65); // head and mane
  for (let k = 0; k < 6; k++) p.px.set(c + s * 2 + Math.round(Math.cos(k) * 6), b - 22 + Math.round(Math.sin(k) * 5), M.stone[1]!); // curls
  p.px.rect(c + s * 4, b - 23, 2, 1, M.dark[1]!); // eye
  p.px.rect(c + s * 5, b - 19, 3, 1, M.dark[2]!); // mouth
  blob(p.px, c + s * 4, b - 2, 3, 3, M.stone, 0.75); // ball under the paw
  p.px.rect(c + s * 3, b - 8, 2, 6, M.stone[2]!); // foreleg
  return fin(p);
}

/** A square wall tower of stone with crenels, an arrow slit and a little tiled roof (≈ 96 px). */
function wallTower(): Sprite {
  const p = propPix(70, 130, 35, 112);
  box(p.px, p.P, [-0.8, -0.8, 0.8, 0.8], 0, 70, (u, v, l) => {
    const row = Math.floor(v / 5);
    const off = row & 1 ? 6 : 0;
    if (v % 5 < 1 || (u + off) % 12 < 1) return snap(M.stone, l - 0.2, u | 0, v | 0, 0);
    return snap(M.stone, l + (hash2(Math.floor((u + off) / 12), row) - 0.5) * 0.22, u | 0, v | 0);
  }, stoneT);
  wall(p.px, p.P, 'left', 0.81, -0.08, 0.08, 36, 52, () => M.dark[1]!);
  wall(p.px, p.P, 'right', 0.81, -0.08, 0.08, 36, 52, () => M.dark[1]!);
  for (const [x, y] of [
    [-0.6, 0.6],
    [0, 0.6],
    [0.6, 0.6],
    [0.6, 0],
    [0.6, -0.6],
  ] as const)
    box(p.px, p.P, [x - 0.17, y - 0.17, x + 0.17, y + 0.17], 70, 78, stoneT, stoneT);
  for (const [x, y] of [
    [-0.5, -0.5],
    [0.5, -0.5],
    [-0.5, 0.5],
    [0.5, 0.5],
  ] as const)
    pole(p.P, p, x, y, 0.06, 70, 92, M.lacquer);
  box(p.px, p.P, [-0.95, -0.95, 0.95, 0.95], 92, 96, tiles(K.greyTile), tiles(K.greyTile));
  box(p.px, p.P, [-0.55, -0.55, 0.55, 0.55], 96, 102, tiles(K.greyTile), tiles(K.greyTile));
  blob(p.px, p.ax, p.ay - 108, 3, 3, M.gold, 0.7);
  return fin(p);
}

/** A wooden training dummy shaped as a man (木人), arms out, on a round base (≈ 46 px). */
function woodMan(): Sprite {
  const p = propPix(36, 60, 18, 54);
  blob(p.px, p.ax, p.ay - 2, 8, 3, M.wood, 0.4);
  box(p.px, p.P, [-0.07, -0.07, 0.07, 0.07], 3, 22, wood(0));
  box(p.px, p.P, [-0.18, -0.12, 0.18, 0.12], 22, 40, wood(0.05, M.deck), wood(0.2, M.deck));
  blob(p.px, p.ax, p.ay - 45, 4, 4, M.deck, 0.55);
  for (const s of [-1, 1]) {
    const [x, y] = p.P(0, 0, 36);
    for (let k = 0; k < 9; k++) p.px.rect(x + s * (3 + k), y + Math.round(k * 0.3), 1, 3, M.deck[k > 6 ? 2 : 3]!);
  }
  p.px.rect(p.ax - 3, p.ay - 33, 7, 1, M.red[3]!); // a sash
  return fin(p);
}

/** An arena fence, 2 tiles along x or y: posts, two rails and a small red pennant on the middle post (≈ 34 px). */
function arenaFence(along: 'x' | 'y'): Sprite {
  const p = propPix(84, 70, 42, 54);
  for (let k = 0; k <= 2; k++) {
    const t = -1 + k;
    const [x, y] = along === 'x' ? [t, 0] : [0, t];
    pole(p.P, p, x, y, 0.07, 0, k === 1 ? 34 : 24, M.wood);
    box(p.px, p.P, [x - 0.1, y - 0.1, x + 0.1, y + 0.1], k === 1 ? 34 : 24, k === 1 ? 36 : 26, flat(M.gold, 0.1));
  }
  for (const z of [9, 19]) {
    const r: [number, number, number, number] = along === 'x' ? [-1, -0.04, 1, 0.04] : [-0.04, -1, 0.04, 1];
    box(p.px, p.P, r, z, z + 3, wood(0.1, K.maroon));
  }
  const [mx, my] = p.P(0, 0, 34);
  for (let yy = 0; yy < 9; yy++) for (let xx = 1; xx < 9 - yy; xx++) p.px.set(mx + xx, my + yy, snap(M.red, 0.6, xx, yy, 0));
  return fin(p);
}

/** A flight of stone stairs with white balustrades either side, rising toward -y (≈ 30 px). */
function stoneStairs(): Sprite {
  const p = propPix(80, 70, 40, 50);
  for (let k = 0; k < 6; k++) box(p.px, p.P, [-0.8, -0.9 + k * 0.3, 0.8, 0.9], 0, 24 - k * 4, stoneT, (u, v, l) => snap(M.stone, l + 0.15, u | 0, v | 0, 0));
  for (const x of [-0.9, 0.9]) {
    for (let k = 0; k <= 6; k++) {
      const y = -0.9 + k * 0.3;
      const z = 24 - Math.max(0, k - 0.5) * 4;
      box(p.px, p.P, [x - 0.07, y - 0.07, x + 0.07, y + 0.07], 0, z + 10, flat(M.linen, 0.05));
    }
  }
  return fin(p);
}

/** A torch on a post: a fire basket with flame (≈ 50 px). */
function torchPost(): Sprite {
  const p = propPix(24, 70, 12, 64);
  pole(p.P, p, 0, 0, 0.05, 0, 42, M.wood);
  const [x, y] = p.P(0, 0, 42);
  blob(p.px, x, y - 1, 4, 2, K.iron, 0.4);
  for (let k = 0; k < 16; k++) {
    const fx = x + Math.round((hash2(k, 1) - 0.5) * 6 * (1 - k / 20));
    p.px.set(fx, y - 3 - Math.floor(k / 2), k < 6 ? M.gold[4]! : k < 11 ? M.red[4]! : M.red[3]!);
  }
  return fin(p);
}

/** A moored sampan with its woven roof (boat painter in buildings.ts), and a fishing boat with its net. */
const sampan = (): Sprite => boat(3, 1);
const netBoat = (): Sprite => boat(3, 0);

export const MAKERS2 = {
  wallX: () => wallSeg('x', false),
  wallY: () => wallSeg('y', false),
  brickWallX: () => wallSeg('x', true),
  brickWallY: () => wallSeg('y', true),
  townWallX: () => townWall('x'),
  townWallY: () => townWall('y'),
  wallGate,
  stele,
  bannerTRed: () => bannerT(M.red, M.gold),
  bannerTBlue: () => bannerT(K.indigo, M.gold),
  pennantRed: () => pennant(M.red),
  pennantJade: () => pennant(M.jade),
  signFrame: () => signFrame(M.dark),
  signFrameBlue: () => signFrame(K.indigo),
  shopSignRed: () => shopSign(M.red),
  shopSignDark: () => shopSign(M.dark),
  flagString,
  lanternString,
  lanternPole2,
  palaceLantern,
  pagodaLantern,
  bambooFenceX: () => bambooFence('x'),
  bambooFenceY: () => bambooFence('y'),
  stoneRailX: () => stoneRail('x'),
  stoneRailY: () => stoneRail('y'),
  archBridge,
  plankBridge,
  shrubPink: () => shrub(PINK, 3),
  shrubWhite: () => shrub(M.linen, 7),
  shrubYellow: () => shrub(K.ochre, 11),
  shrubGreen: () => shrub(null, 5),
  flowerBedRed: () => flowerBed(M.red),
  flowerBedGold: () => flowerBed(K.ochre),
  pottedPine: () => pottedPlant(0),
  pottedBloom: () => pottedPlant(1),
  bambooClump: () => bambooClump(3),
  bambooClump2: () => bambooClump(17),
  bambooPile,
  rocks: () => rockCluster(2),
  rocks2: () => rockCluster(9),
  rockPillar,
  cliffChunk,
  lilyPads: () => lilyPads(1),
  lilyPads2: () => lilyPads(6),
  cascade,
  stoneBasin,
  stupa,
  produce: () => produce(0),
  produce2: () => produce(2),
  hangingRack,
  pottery,
  wheelbarrow,
  coveredWagon,
  teaTable,
  shelf,
  vases,
  target,
  warDrum,
  plumPosts,
  stoneWeights,
  altar,
  roadShrine,
  wineJars,
  baskets,
  umbrellaRed: () => umbrella(M.red),
  umbrellaOchre: () => umbrella(K.ochre),
  birdCage,
  canopyRed: () => canopy(M.red),
  canopyCream: () => canopy(K.cream),
  dyeRack,
  dock,
  crateStack,
  sackStack,
  barrelStack,
  leaves: () => groundBits('leaves'),
  petals: () => groundBits('petals'),
  puddle: () => groundBits('puddle'),
  snow: () => groundBits('snow'),
  sampan,
  netBoat,
  lionL: () => stoneLion(false),
  lionR: () => stoneLion(true),
  wallTower,
  woodMan,
  arenaFenceX: () => arenaFence('x'),
  arenaFenceY: () => arenaFence('y'),
  stoneStairs,
  torchPost,
} satisfies Record<string, () => Sprite>;

export type PropId2 = keyof typeof MAKERS2;

